import { z } from 'zod';
import { IdSchema, MoneySchema } from './common.js';

export const GranularitySchema = z.enum(['NONE', 'COLOR', 'COLOR_SIZE']);
export type Granularity = z.infer<typeof GranularitySchema>;
export const LifecycleSchema = z.enum(['ACTIVA', 'AGOTANDOSE', 'TEMPORAL', 'DESCONTINUADA', 'ARCHIVADA']);
export type Lifecycle = z.infer<typeof LifecycleSchema>;

export const PricesSchema = z.object({
  unit_cost: MoneySchema.optional(),
  internal_price: MoneySchema.optional(),
  wholesale_price: MoneySchema.optional(),
  retail_price: MoneySchema.optional(),
  promo_price: MoneySchema.optional(),
  min_sale_price: MoneySchema.optional(),
});

export const VariantInputSchema = z.object({
  color: z.string().trim().max(40).optional(),
  size: z.string().trim().max(20).optional(),
});

export const ProductSchema = z.object({
  id: z.number().int(),
  ref: z.string(),
  barcode: z.string().nullable(),
  name: z.string(),
  brand: z.string(),
  line: z.string(),
  category: z.string(),
  gender: z.string(),
  origin: z.string(),
  lifecycle: LifecycleSchema,
  min_stock: z.number().int(),
  stock_granularity: GranularitySchema,
  merged_into_product_id: z.number().int().nullable(),
  notes: z.string(),
  active: z.boolean(),
  internal_price: z.number().nullable(),
  wholesale_price: z.number().nullable(),
  retail_price: z.number().nullable(),
  promo_price: z.number().nullable(),
  min_sale_price: z.number().nullable(),
  /** Solo con permiso cost.view; si no, null. */
  unit_cost: z.number().nullable(),
  variant_count: z.number().int(),
  /** Algún precio de venta quedó por debajo del costo (anomalía; no revela el costo). */
  below_cost: z.boolean(),
  /** Cuáles precios están por debajo del costo: internal_price, wholesale_price, retail_price, promo_price. */
  below_cost_fields: z.array(z.string()).optional(),
  created_at: z.string(),
  updated_at: z.string(),
});
export type Product = z.infer<typeof ProductSchema>;

export const VariantSchema = z.object({
  id: z.number().int(),
  product_id: z.number().int(),
  ref: z.string(),
  product_name: z.string(),
  color: z.string().nullable(),
  size: z.string().nullable(),
  sku: z.string(),
  active: z.boolean(),
});
export type Variant = z.infer<typeof VariantSchema>;

export const PriceHistoryEntrySchema = z.object({
  id: z.number().int(),
  effective_at: z.string(),
  unit_cost: z.number().nullable(),
  internal_price: z.number(),
  wholesale_price: z.number(),
  retail_price: z.number(),
  promo_price: z.number(),
  min_sale_price: z.number(),
  source: z.string(),
  user_id: z.number().int().nullable(),
  user_name: z.string().nullable(),
});

export type PriceHistoryEntry = z.infer<typeof PriceHistoryEntrySchema>;

export const ProductDetailSchema = ProductSchema.extend({
  variants: z.array(VariantSchema),
  price_history: z.array(PriceHistoryEntrySchema),
  /** Sin existencias, movimientos, documentos ni curvas: todavía se puede cambiar su control de inventario. */
  can_change_control: z.boolean(),
});
export type ProductDetail = z.infer<typeof ProductDetailSchema>;

export const ProductListQuerySchema = z.object({
  q: z.string().trim().max(80).optional(),
  lifecycle: LifecycleSchema.optional(),
  include_archived: z.coerce.boolean().default(false),
  /** Solo referencias con algún precio por debajo del costo (alerta de Gerencia). */
  below_cost: z.enum(['true', 'false']).default('false').transform((v) => v === 'true'),
  limit: z.coerce.number().int().min(1).max(500).default(200),
  /** Paginación: filas a saltar. El total de la consulta viaja en la cabecera X-Total-Count. */
  offset: z.coerce.number().int().min(0).default(0),
});

/** Siguiente referencia numérica sugerida al crear un producto (editable). */
export const NextRefSchema = z.object({ ref: z.string() });

export const CreateProductBodySchema = z.object({
  ref: z.string().trim().regex(/^[A-Za-z0-9._/-]{1,30}$/),
  barcode: z.string().trim().max(40).optional(),
  name: z.string().trim().min(1).max(120),
  brand: z.string().trim().max(60).default(''),
  line: z.string().trim().max(60).default(''),
  category: z.string().trim().max(60).default(''),
  gender: z.string().trim().max(20).default(''),
  origin: z.string().trim().max(40).default(''),
  min_stock: z.number().int().min(0).default(0),
  stock_granularity: GranularitySchema.default('NONE'),
  notes: z.string().max(2000).default(''),
  prices: PricesSchema.default({}),
  variants: z.array(VariantInputSchema).default([]),
  confirm_similar: z.boolean().default(false),
});
export type CreateProductBody = z.infer<typeof CreateProductBodySchema>;

export const UpdateProductBodySchema = CreateProductBodySchema.omit({ ref: true, barcode: true, stock_granularity: true, variants: true }).partial();

export const ChangeControlBodySchema = z.object({
  stock_granularity: GranularitySchema,
  variants: z.array(VariantInputSchema).max(500).default([]),
});

export const LifecycleBodySchema = z.object({
  lifecycle: LifecycleSchema,
  reason: z.string().trim().max(200).default(''),
});

export const AddVariantsBodySchema = z.object({ variants: z.array(VariantInputSchema).min(1) });

// ----- Curvas (paquetes de compra) -----
export const CurveLineSchema = z.object({
  variant_id: z.number().int(),
  units: z.number().int(),
  sku: z.string(),
  ref: z.string(),
  product_name: z.string(),
  color: z.string().nullable(),
  size: z.string().nullable(),
});
export const CurveSchema = z.object({
  id: z.number().int(),
  code: z.string(),
  name: z.string(),
  active: z.boolean(),
  lines: z.array(CurveLineSchema),
  total_units: z.number().int(),
});
export type Curve = z.infer<typeof CurveSchema>;
export const SaveCurveBodySchema = z.object({
  code: z.string().trim().regex(/^[A-Za-z0-9._-]{1,30}$/),
  name: z.string().trim().min(1).max(80),
  active: z.boolean().default(true),
  lines: z.array(z.object({ variant_id: IdSchema, units: z.number().int().min(1) })).min(1),
});

// ----- Importación de catálogo / precios -----
export const CatalogImportRowSchema = z.object({
  row: z.number().int(),
  ref: z.string().trim(),
  name: z.string().trim().max(120).optional(),
  brand: z.string().trim().max(60).optional(),
  category: z.string().trim().max(60).optional(),
  gender: z.string().trim().max(20).optional(),
  unit_cost: MoneySchema.nullable().optional(),
  internal_price: MoneySchema.nullable().optional(),
  wholesale_price: MoneySchema.nullable().optional(),
  retail_price: MoneySchema.nullable().optional(),
  promo_price: MoneySchema.nullable().optional(),
});
export type CatalogImportRow = z.infer<typeof CatalogImportRowSchema>;
export const CatalogImportBodySchema = z.object({
  source: z.string().trim().max(120).default('excel'),
  rows: z.array(CatalogImportRowSchema).min(1).max(5000),
});
export const CatalogImportResultSchema = z.object({
  mode: z.enum(['preview', 'commit']),
  new: z.number().int(),
  updated: z.number().int(),
  unchanged: z.number().int(),
  errors: z.number().int(),
  rows: z.array(z.object({
    row: z.number().int().nullable(),
    ref: z.string(),
    status: z.enum(['new', 'update', 'unchanged', 'error']),
    reason: z.string().nullable(),
  })),
  /** Referencias con algún precio de venta por debajo del costo (aviso para Gerencia; no bloquea). */
  below_cost_refs: z.array(z.string()),
});
export type CatalogImportResult = z.infer<typeof CatalogImportResultSchema>;
